/**
 * #815 · Lo que la pantalla **Gasto de IA** decide y no es dibujo: los periodos del selector, cómo
 * se escriben las cifras y qué avisos acompañan al total. Aparte de la pantalla para poder probarlo
 * (`scripts/prueba-gasto-ia.js`) sin montar Framework7.
 *
 * Las cifras vienen hechas de simecal-ai (`GET /movilidad/ai/consumo/resumen`): el total es la suma
 * del desglose y aquí no se recalcula nada. Lo único que se suma en el front es el reparto por
 * proveedor, y se suma en millonésimas, como el servidor, para que cuadre con el total.
 */

// Los días los cuenta el servidor en hora de Madrid; el selector también, para que «mes en curso»
// no cambie de mes antes o después según el reloj del ordenador desde el que se mire.
const ZONA = 'Europe/Madrid';

/** Hoy en Madrid como `{ anio, mes, dia }`. */
function hoyEnMadrid(ahora) {
    const partes = new Intl.DateTimeFormat('en-US', {
        timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(ahora || new Date());
    const v = {};
    partes.forEach(p => { v[p.type] = p.value; });
    return { anio: Number(v.year), mes: Number(v.month), dia: Number(v.day) };
}

function aTexto(anio, mes, dia) {
    // Normaliza desbordes (día 0 = último del mes anterior) con la aritmética de Date en UTC.
    const d = new Date(Date.UTC(anio, mes - 1, dia));
    return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0') + '-'
        + String(d.getUTCDate()).padStart(2, '0');
}

export const PERIODOS = [
    { id: 'mes', etiqueta: 'Mes en curso' },
    { id: 'mes-anterior', etiqueta: 'Mes anterior' },
    { id: '7', etiqueta: 'Últimos 7 días' },
    { id: '30', etiqueta: 'Últimos 30 días' },
    { id: 'anio', etiqueta: 'Año en curso' },
    { id: 'otro', etiqueta: 'Otro periodo…' }
];

export const PERIODO_POR_DEFECTO = 'mes';

/**
 * `{ desde, hasta }` (AAAA-MM-DD, los dos incluidos) de un periodo del selector. `otro` no se
 * calcula: lo escribe la persona y devuelve `null`.
 */
export function rangoDe(id, ahora) {
    const h = hoyEnMadrid(ahora);
    const hoy = aTexto(h.anio, h.mes, h.dia);
    switch (id) {
    case 'mes': return { desde: aTexto(h.anio, h.mes, 1), hasta: hoy };
    case 'mes-anterior': return { desde: aTexto(h.anio, h.mes - 1, 1), hasta: aTexto(h.anio, h.mes, 0) };
    case '7': return { desde: aTexto(h.anio, h.mes, h.dia - 6), hasta: hoy };
    case '30': return { desde: aTexto(h.anio, h.mes, h.dia - 29), hasta: hoy };
    case 'anio': return { desde: aTexto(h.anio, 1, 1), hasta: hoy };
    default: return null;
    }
}

const PATRON_DIA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Por qué no se puede consultar un periodo escrito a mano, o `null` si se puede. Se dice junto a
 * los campos de fecha: un «Consultar» que no hace nada al pulsarlo es un botón mudo (§9).
 */
export function motivoPeriodoInvalido(desde, hasta) {
    if (!desde || !hasta) return 'Elige las dos fechas, «desde» y «hasta».';
    if (!PATRON_DIA.test(desde) || !PATRON_DIA.test(hasta)) return 'Las fechas no tienen un formato válido.';
    if (desde > hasta) return '«Desde» no puede ser posterior a «hasta».';
    return null;
}

const numero = new Intl.NumberFormat('es-ES');
const eur2 = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd2 = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Euros al céntimo. Lo que no llega a medio céntimo se escribe «< 0,01 €» y no «0,00 €»: un
 * servicio que ha gastado algo no puede leerse como si no hubiera gastado nada.
 */
export function eur(valor) {
    return importe(valor, eur2, '€');
}

/** Lo mismo en dólares, que es en lo que facturan los proveedores. */
export function usd(valor) {
    return importe(valor, usd2, '$');
}

function importe(valor, formato, simbolo) {
    const n = Number(valor);
    if (!Number.isFinite(n)) return '—';
    if (n > 0 && n < 0.005) return '< 0,01 ' + simbolo;
    return formato.format(n);
}

export function entero(valor) {
    const n = Number(valor);
    return Number.isFinite(n) ? numero.format(n) : '—';
}

export function porcentaje(valor) {
    if (valor === null || valor === undefined || !Number.isFinite(Number(valor))) return '—';
    return numero.format(Number(valor)) + ' %';
}

const NOMBRES_PROVEEDOR = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
    ollama: 'Ollama (local)',
    elevenlabs: 'ElevenLabs'
};

export function nombreProveedor(proveedor) {
    return NOMBRES_PROVEEDOR[proveedor] || proveedor || 'Sin proveedor';
}

/**
 * Gasto por proveedor, a partir del desglose por modelo. Es la fila que se compara con cada
 * factura (OpenAI, Anthropic, Google), así que va en dólares además de en euros.
 */
export function porProveedor(modelos) {
    const mapa = {};
    (modelos || []).forEach((m) => {
        const clave = m.proveedor || 'desconocido';
        if (!mapa[clave]) {
            mapa[clave] = { proveedor: clave, nombre: nombreProveedor(clave), llamadas: 0, llamadasSinCoste: 0, microsEur: 0, microsUsd: 0 };
        }
        const p = mapa[clave];
        p.llamadas += Number(m.llamadas) || 0;
        p.llamadasSinCoste += Number(m.llamadasSinCoste) || 0;
        p.microsEur += Math.round((Number(m.costeEur) || 0) * 1e6);
        p.microsUsd += Math.round((Number(m.costeUsd) || 0) * 1e6);
    });
    return Object.keys(mapa).map(k => mapa[k])
        .sort((a, b) => (b.microsEur - a.microsEur) || (b.llamadas - a.llamadas) || a.nombre.localeCompare(b.nombre))
        .map(p => ({
            proveedor: p.proveedor,
            nombre: p.nombre,
            llamadas: p.llamadas,
            llamadasSinCoste: p.llamadasSinCoste,
            costeEur: p.microsEur / 1e6,
            costeUsd: p.microsUsd / 1e6
        }));
}

function fechaLarga(valor) {
    const d = new Date(valor);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('es-ES', { timeZone: ZONA, day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

function diaDeMadrid(valor) {
    const d = new Date(valor);
    if (isNaN(d.getTime())) return null;
    const h = hoyEnMadrid(d);
    return aTexto(h.anio, h.mes, h.dia);
}

/**
 * Lo que la cifra NO cuenta, dicho junto al total: si se callara, quien compare con la factura del
 * proveedor vería un descuadre sin saber de dónde sale. Cada aviso es una frase completa.
 */
export function avisosDe(resumen) {
    const avisos = [];
    if (!resumen || !resumen.total) return avisos;
    const total = resumen.total;
    const periodo = resumen.periodo || {};

    if (!resumen.primerRegistro) {
        avisos.push('Todavía no hay ninguna llamada a IA registrada. El registro empezó a funcionar con la tarea #814;'
            + ' lo anterior no se apuntaba.');
    } else {
        const primerDia = diaDeMadrid(resumen.primerRegistro);
        if (primerDia && periodo.desde && primerDia > periodo.desde) {
            avisos.push('El registro empieza el ' + fechaLarga(resumen.primerRegistro) + ': el gasto anterior a esa fecha'
                + ' no se apuntaba y no está en estas cifras, así que este periodo sale más corto de lo que fue.');
        }
    }
    if (total.llamadasSinCoste > 0) {
        avisos.push(entero(total.llamadasSinCoste) + ' llamada(s) no tienen coste calculable y no suman en el total:'
            + ' el proveedor no informa de su consumo (indexación de documentos con Gemini, voz) o su modelo no tiene'
            + ' tarifa registrada. Están contadas en «Llamadas».');
    }
    if (total.llamadasCambioPorDefecto > 0) {
        avisos.push(entero(total.llamadasCambioPorDefecto) + ' llamada(s) se pasaron a euros con el cambio por defecto'
            + ' (0,86 € por dólar) porque no estaba registrada la property IA_EUR_POR_USD. La puede registrar un'
            + ' administrador en Ajustes → Properties; las llamadas siguientes usarán ese cambio.');
    }
    return avisos;
}

export default {
    PERIODOS,
    PERIODO_POR_DEFECTO,
    rangoDe,
    motivoPeriodoInvalido,
    eur,
    usd,
    entero,
    porcentaje,
    nombreProveedor,
    porProveedor,
    avisosDe
};
